import { IconButton } from "@mui/material";
import { AnimatePresence, motion } from "motion/react";
import { useEffect } from "react";
import { createPortal } from "react-dom";
import { TbUser, TbX } from "react-icons/tb";

const backdropVariants = {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    exit: { opacity: 0 },
};

const cardVariants = {
    initial: { opacity: 0, scale: 0.9, y: 40 },
    animate: { opacity: 1, scale: 1, y: 0 },
    exit: { opacity: 0, scale: 0.95, y: 20 },
};

export const PassengerPhotoDialog = ({
    open,
    onClose,
    photoUrl,
    name,
    languageLabel,
}: {
    open: boolean;
    onClose: () => void;
    photoUrl: string | null;
    name: string;
    languageLabel?: string | null;
}) => {
    useEffect(() => {
        if (!open) return;
        const original = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            document.body.style.overflow = original;
        };
    }, [open]);

    const content = (
        <AnimatePresence>
            {open && photoUrl && (
                <motion.div
                    variants={backdropVariants}
                    initial="initial"
                    animate="animate"
                    exit="exit"
                    transition={{ duration: 0.25 }}
                    className="fixed inset-0 z-[2000] flex items-center justify-center p-2 sm:p-4 bg-gradient-to-br from-elitLightBlue/50 via-blue-200/60 to-purple-200/50 backdrop-blur-md"
                    onClick={onClose}
                >
                    <motion.div
                        variants={cardVariants}
                        transition={{ type: "spring", stiffness: 220, damping: 24 }}
                        className="relative w-full h-full sm:max-w-2xl sm:max-h-[95vh] bg-white/95 rounded-3xl shadow-2xl shadow-elitDarkBlue/40 overflow-hidden flex flex-col"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="absolute top-3 right-3 z-10">
                            <IconButton
                                size="small"
                                onClick={onClose}
                                className="!bg-white/80 backdrop-blur-sm hover:!bg-white shadow-md"
                            >
                                <TbX className="text-elitDarkBlue" />
                            </IconButton>
                        </div>

                        <div className="bg-gradient-to-b from-elitLightBlue/30 to-transparent px-5 pt-4 pb-3 flex items-center gap-2 shrink-0">
                            <div className="w-9 h-9 rounded-full bg-elitLightBlue/30 flex items-center justify-center text-elitDarkBlue shrink-0">
                                <TbUser className="text-lg" />
                            </div>
                            <span className="font-semibold text-elitDarkBlue text-base truncate">{name}</span>
                            {languageLabel && (
                                <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-white/70 text-elitDarkBlue border border-elitLightBlue/40 whitespace-nowrap shrink-0">
                                    {languageLabel}
                                </span>
                            )}
                            <span className="grow" />
                            <span className="w-10 shrink-0" />
                        </div>

                        <div className="flex-1 flex items-center justify-center p-3 pt-0 bg-gradient-to-b from-white to-elitLightBlue/10 min-h-0">
                            <motion.img
                                src={photoUrl}
                                alt={name}
                                initial={{ opacity: 0, scale: 1.04 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{ delay: 0.1, duration: 0.35 }}
                                className="max-w-full max-h-full w-auto h-auto object-contain rounded-2xl shadow-md shadow-black/10"
                            />
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );

    return createPortal(content, document.body);
};
